import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, take } from 'rxjs';

import { SharedModule } from '~shared/shared.module';
import { MaintenanceService } from '~core/services/maintenance.service';
import { AuthService } from '~core/services/auth.service';
import { MaintenanceRepository } from '~shared/store/maintenance/maintenance.repository';
import { MaintenanceStatus } from '~shared/types/maintenance.types';
import { UserRole, UserType } from '~shared/enums/user.enum';
import { AlertService } from '~core/services/alert.service';

@UntilDestroy()
@Component({
	selector: 'app-maintenance-admin-page',
	standalone: true,
	imports: [CommonModule, SharedModule],
	templateUrl: './maintenance-admin-page.component.html',
	styleUrls: ['./maintenance-admin-page.component.scss'],
})
export class MaintenanceAdminPageComponent implements OnInit {
	public status$: Observable<MaintenanceStatus> = this.maintenanceRepository.status$;
	public loading = true;
	public saving = false;

	public form = this.fb.group({
		enabled: [false],
		message: [''],
		endsAt: [''], // datetime-local (browser local time)
	});

	constructor(
		private readonly fb: FormBuilder,
		private readonly maintenanceService: MaintenanceService,
		private readonly maintenanceRepository: MaintenanceRepository,
		private readonly authService: AuthService,
		private readonly router: Router,
		private readonly alertService: AlertService,
	) {}

	public ngOnInit(): void {
		// Defence in depth: the menu only shows this for internal admins and the
		// backend rejects non-admins, but don't render the controls on a deep link.
		if (!this.isInternalAdmin()) {
			void this.router.navigate(['/']);
			return;
		}

		// Prefill from a fresh read so the default return time comes from the
		// SERVER clock (serverTime), not the user's machine. Falls back to the
		// client clock only if the server time is unavailable (endpoint down).
		this.maintenanceService
			.fetchStatus()
			.pipe(take(1), untilDestroyed(this))
			.subscribe({
				next: (status) => {
					const now = status.serverTime ?? new Date().toISOString();
					this.form.patchValue({
						enabled: status.enabled,
						message: status.message ?? '',
						endsAt: this.toLocalInput(status.endsAt ?? now),
					});
					this.loading = false;
				},
				error: () => (this.loading = false),
			});
	}

	/** Opens the public maintenance page in a new tab with the drafted values. */
	public get previewUrl(): string {
		const { message, endsAt } = this.form.getRawValue();
		const params = new URLSearchParams({ preview: '1' });
		if (message) {
			params.set('msg', message);
		}
		if (endsAt) {
			params.set('endsAt', this.toIso(endsAt));
		}
		return `/maintenance?${params.toString()}`;
	}

	public submit(): void {
		if (this.saving) {
			return;
		}

		const { enabled, message, endsAt } = this.form.getRawValue();
		const status: MaintenanceStatus = {
			enabled: !!enabled,
			message: message?.trim() || null,
			endsAt: endsAt ? this.toIso(endsAt) : null,
		};

		this.saving = true;
		this.maintenanceService
			.setStatus(status)
			.pipe(untilDestroyed(this))
			.subscribe({
				next: () => {
					this.saving = false;
					this.alertService.success(
						status.enabled
							? 'Onderhoudsmodus ingeschakeld.'
							: 'Onderhoudsmodus uitgeschakeld.',
					);
				},
				// Errors are toasted by the global error interceptor.
				error: () => (this.saving = false),
			});
	}

	private isInternalAdmin(): boolean {
		const user = this.authService.currentUser$.value;
		return user?.userType === UserType.INTERNAL && user?.userRole === UserRole.ADMIN;
	}

	/** ISO 8601 (UTC) -> 'YYYY-MM-DDTHH:mm' in local time for datetime-local. */
	private toLocalInput(iso: string): string {
		const date = new Date(iso);
		if (isNaN(date.getTime())) {
			return '';
		}
		const pad = (n: number) => String(n).padStart(2, '0');
		return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
	}

	/** Local datetime-local string -> ISO 8601 (UTC). */
	private toIso(local: string): string {
		return new Date(local).toISOString();
	}
}
